import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Trans } from "react-i18next";
import { CDN_IMG_URL, CDN_STATIC_URL, LOCALSTORAGE_KEY } from "@config";
import { PageListType, TableData } from "@models/common";
import { RegionOption } from "@store/configurations/types";
import { last } from "@utils/array";
import { CURRENCY_SYMBOL } from "@src/config/country";

export const formateDate = (date: Dayjs | number | string, needYear: boolean = true) => {
  if (date) {
    if (typeof date === "string" || typeof date === "number") {
      if (needYear === true) {
        return dayjs(date).format("YYYY-MM-DD");
      } else {
        return dayjs(date).format("MM-DD");
      }
    } else {
      if (needYear === true) {
        return date.format("YYYY-MM-DD");
      } else {
        return date.format("MM-DD");
      }
    }
  }
  return "";
};

export const formatReqDate = (date: Dayjs | string) => {
  if (typeof date === "string") {
    return dayjs(date).format("YYYY-MM-DD");
  } else if (date?.format) {
    return date.format("YYYY-MM-DD");
  } else {
    return date;
  }
};

export const formatImgUrl = (type: PageListType, id?: string) => {
  const typeMap: { [key: string]: string } = {
    shop: "seller",
    livestream: "live",
  };
  const urlType = typeMap[type] || type;
  const imageMap: { [key: string]: string } = {
    creator: "avatar_medium.png",
    video: "cover.png",
    product: "cover.png",
    seller: "logo.png",
    shop: "logo.png",
    live: "cover.png",
    livestream: "cover.png",
  };
  if (imageMap[type] && id) {
    return `${CDN_IMG_URL}/tiktok.${urlType}/${id}/${imageMap[type]}`;
  }
  return "";
};

export const formatCategoryListValue = (value: string[][]) => {
  let result = [];
  for (let item of value) {
    result.push(last(item));
  }
  return result;
};

export const formatTableRequestParams = (tableData: TableData<any>) => {
  return {
    pageNo: tableData.pagination!.current,
    pageSize: tableData.pagination!.pageSize,
    sort: formatTableSorter(tableData.sorter as any),
  };
};

export const formatTableSorter = (sorter: { field: string; order: string }) => {
  let sort = undefined;
  if (sorter && sorter.field) {
    sort = [];
    sort.push({ field: sorter.field, type: sorter.order === "ascend" ? "ASC" : "DESC" });
  }
  return sort;
};

export const formatCurrencyDec = (text: string) => {
  const currency = localStorage.getItem(LOCALSTORAGE_KEY.CURRENCY) || "";

  // @ts-ignore
  let currencySymbol = CURRENCY_SYMBOL[currency];
  currencySymbol = currencySymbol ? `(${currencySymbol})` : "";
  return (
    <>
      <Trans>{text}</Trans>
      {currencySymbol}
    </>
  );
};

export const formatCagegory = ({
  pri_cate_id,
  sec_cate_id,
  ter_cate_id,
}: {
  pri_cate_id?: string;
  sec_cate_id?: string;
  ter_cate_id?: string;
}) => {
  let cates = [];
  if (pri_cate_id) {
    cates.push(pri_cate_id);
  }
  if (sec_cate_id) {
    cates.push(sec_cate_id);
  }
  if (ter_cate_id) {
    cates.push(ter_cate_id);
  }
  return cates.join(" > ");
};

export const formatNumber = (number: number, toFixed: number = 2) => {
  if (number === undefined) return "";
  let num = Number(number);
  if (Number.isNaN(num)) return number;

  let language = localStorage.getItem(LOCALSTORAGE_KEY.LANGUAGE) || "en-US";
  const rangeMap: { [key: string]: any[] } = {
    "zh-CN": [
      { value: 100000000, unit: "亿" },
      { value: 10000, unit: "万" },
    ],
    "ja-JP": [
      { value: 100000000, unit: "億" },
      { value: 10000, unit: "万" },
    ],
    "id-ID": [
      { value: 1000000000000, unit: "T" },
      { value: 1000000000, unit: "M" },
      { value: 1000000, unit: "JT" },
      { value: 1000, unit: "RB" },
    ],
    "vi-VN": [
      { value: 1000000000, unit: "tỉ" },
      { value: 1000000, unit: "tr" },
      { value: 1000, unit: "k" },
    ],
    "th-TH": [
      { value: 1000000, unit: " ล้าน" },
      { value: 100000, unit: " แสน" },
      { value: 10000, unit: " หมื่น" },
      { value: 1000, unit: " พัน" },
    ],
    "en-US": [
      { value: 1000000000, unit: "b" },
      { value: 1000000, unit: "m" },
      { value: 1000, unit: "k" },
    ],
    "es-ES": [
      { value: 1000000000, unit: " mil mill." },
      { value: 1000000, unit: " mill." },
      { value: 1000, unit: " mil" },
    ],
    "pt-BR": [
      { value: 1000000000, unit: " bi" },
      { value: 10000, unit: " mi" },
      { value: 1000, unit: " mil" },
    ],
  };

  const ranges = rangeMap[language] || rangeMap["en-US"];

  for (let i = 0; i < ranges.length; i++) {
    const compareValue = ranges[i].value;
    if (num >= compareValue) {
      if (language === "es-ES") {
        return new Intl.NumberFormat().format(Number((num / compareValue).toFixed(toFixed))) + ranges[i].unit;
      } else {
        return new Intl.NumberFormat(language).format(Number((num / compareValue).toFixed(toFixed))) + ranges[i].unit;
      }
    }
  }
  if (Number.isInteger(num)) {
    return num;
  } else {
    return num.toFixed(toFixed);
  }
};

// 比如：5000-50000, >50000, <50000格式化
export const formatRangeNumber = (num: string, unit: string = "") => {
  if (!num || typeof num !== "string") return;
  let result;
  try {
    if (num === "All" || num === "No-ad") {
      return <Trans>{num}</Trans>;
    }
    result = num.replace(/(.*)([<>-])(.*)/, function (_, p1, p2, p3) {
      return (p1 ? formatNumber(p1) + unit : "") + p2 + (p3 ? formatNumber(p3) + unit : "");
    });
  } catch (error) {
    console.error(error);
  }
  return result;
};

export const formatCurrency = (num: number) => {
  if (isNaN(num)) {
    return "-";
  }
  const currency = localStorage.getItem(LOCALSTORAGE_KEY.CURRENCY);
  if (currency) {
    let currencySymbol = CURRENCY_SYMBOL[currency];
    if (currencySymbol) {
      if (currency === "JPY") {
        return formatNumber(num) + currencySymbol;
      }
      return currencySymbol + formatNumber(num);
    }
  }
  return formatNumber(num);
};

// 向上取保留两位小数
export const formatPriceCeil = (price: number) => {
  return (Math.ceil(price * 100) / 100).toFixed(2);
};

export const formatCurrencyPrice = (num: number) => {
  if (isNaN(num)) {
    return "-";
  }
  const currency = localStorage.getItem(LOCALSTORAGE_KEY.CURRENCY);
  if (currency) {
    let currencySymbol = CURRENCY_SYMBOL[currency];
    if (currencySymbol) {
      if (currency === "JPY") {
        return formatNumberNormal(num) + currencySymbol;
      }
      return currencySymbol + formatNumberNormal(num);
    }
  }
  return formatNumberNormal(num);
};

export const formateReqCateIDList = (listValue: string[][]) => {
  return listValue.map((item) => last(item));
};

export const formatHandle = (text?: string) => {
  if (text) {
    return "@" + text;
  }
  return "";
};

export const formatSort = (tableData: TableData<unknown>) => {
  let sort = undefined;
  const sorter = tableData?.sorter as any;
  if (sorter?.field) {
    sort = [];
    sort.push({ field: sorter.field, type: sorter.order === "ascend" ? "ASC" : "DESC" });
  }
  return sort;
};

export const getRegionIcon = (regions: RegionOption[], value: string) => {
  return regions.find((region) => region.value === value)?.icon || "";
};

export const formatContactURL = (code: string) => {
  // return `${CDN_STATIC_URL}/contacts/${code}.png`;
  // return `${CDN_STATIC_URL}/contacts1/${code}.png`;
  return `${CDN_STATIC_URL}/contacts2/${code}.png`;
};

export const formatSecondDuration = (second: number) => {
  const formatNum = (num: number) => {
    if ((num + "").length === 1) {
      return "0" + num;
    }
    return num;
  };

  if (second < 60) {
    return `00:00`;
  } else if (second < 3600) {
    const min = parseInt(String(second / 60));
    return `00:${formatNum(min)}`;
  } else {
    const hour = parseInt(String(second / 3600));
    const min = parseInt(String((second - hour * 3600) / 60));
    return `${formatNum(hour)}:${formatNum(min)}`;
  }
};

// 3500 => '3,500'
export const formatNumberNormal = (number: number, isIndonesia: boolean = false) => {
  if (isIndonesia) {
    return new Intl.NumberFormat("id-ID").format(number);
  } else {
    return new Intl.NumberFormat().format(number);
  }
};

export const formatDurationTime = (time: number) => {
  if (time >= 0) {
    let seconds: string | number = Math.floor(time % 60);
    let minitus: string | number = Math.floor(time / 60) % 60;
    let hours: string | number = Math.floor(time / 3600);

    seconds = seconds < 10 ? `0${seconds}` : seconds;
    minitus = minitus < 10 ? `0${minitus}` : minitus;
    hours = hours < 10 ? `0${hours}` : hours;

    if (hours == 0) {
      return `${minitus}:${seconds}`;
    }
    return `${hours}:${minitus}:${seconds}`;
  }
  return "00:00";
};

dayjs.extend(utc);
dayjs.extend(timezone);

export function getCurrentTimeZoneDate() {
  // 获取当前时区的偏移量（小时）
  const currentDate = dayjs();
  const timezoneOffset = currentDate.utcOffset() / 60; // 转换为小时

  // 获取格式化的 GMT 时区信息
  const gmtOffset = `GMT${timezoneOffset >= 0 ? "+" : ""}${timezoneOffset}`;

  // 格式化日期为 YYYY/MM/DD 格式
  const formattedDate = currentDate.format("YYYY/MM/DD");

  // 返回最终的字符串：如 "GMT+7 2024/09/20"
  return `${gmtOffset} ${formattedDate}`;
}
